import { useState, useRef, useEffect } from "react";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";

const countries = getCountries().map((code) => ({
  code: `+${getCountryCallingCode(code)}`,
  countryCode: code,
  label: new Intl.DisplayNames(["en"], { type: "region" }).of(code),
}));

function useUserCountryCode() {
  const [countryCode, setCountryCode] = useState(null);

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.country_code) {
          setCountryCode(data.country_code);
        } else {
          setCountryCode("US");
        }
      })
      .catch(() => setCountryCode("US"));
  }, []);

  return countryCode;
}

export default function PhoneField({
  label,
  required,
  value, // This will now be the full phone number with country code
  onChange,
  placeholder,
}) {
  // Extract country code and local number from the value
  const userCountryCode = useUserCountryCode();

  const [selectedCode, setSelectedCode] = useState(null);
  const [localNumber, setLocalNumber] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef();

  useEffect(() => {
    if (!value && !userCountryCode) return;

    const defaultCountry =
      countries.find((c) => c.countryCode === userCountryCode) || countries[0];

    const countryFromValue = countries.find((c) => value?.startsWith(c.code));

    const selected = countryFromValue || defaultCountry;
    const number = value?.replace(selected.code, "") || "";

    setSelectedCode(selected);
    setLocalNumber(number);
  }, [value, userCountryCode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountryChange = (country) => {
    setSelectedCode(country);
    setShowDropdown(false);
    // Combine new country code with existing local number
    onChange({ target: { value: country.code + localNumber } });
  };

  const handleNumberChange = (e) => {
    const numericOnly = e.target.value.replace(/\D/g, "").slice(0, 15);
    setLocalNumber(numericOnly);
    // Combine selected country code with new local number
    onChange({ target: { value: selectedCode.code + numericOnly } });
  };

  const filteredCountries = countries.filter(
    (c) =>
      c.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.includes(searchTerm)
  );
  if (!selectedCode) return null;
  return (
    <div className="mb-6">
      <label className="block text-sm font-medium mb-1 text-gray-900">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex items-center relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-gray-900 bg-gray-100 border border-gray-300 rounded-s-lg hover:bg-gray-200 focus:outline-none dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white dark:border-gray-600"
          type="button"
        >
          {selectedCode.code}
          <svg
            className="w-2.5 h-2.5 ml-2"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 1 4 4 4-4"
            />
          </svg>
        </button>

        {showDropdown && (
          <div
            id="dropdown-phone"
            className="absolute top-full left-0 mt-2 z-10 bg-white divide-y divide-gray-100 rounded-lg shadow w-64 max-h-60 overflow-auto dark:bg-gray-700"
          >
            <div className="p-2">
              <input
                type="text"
                placeholder="Search country"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded dark:bg-gray-600 dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ul className="py-2 text-sm text-gray-900 dark:text-gray-200">
              {filteredCountries.map((country) => (
                <li key={`${country.label}-${country.code}`}>
                  <button
                    type="button"
                    onClick={() => handleCountryChange(country)}
                    className="inline-flex w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                  >
                    {country.label} ({country.code})
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <input
          type="text"
          pattern="\d*"
          inputMode="numeric"
          id="phone-input"
          className="block p-2.5 w-full z-20 text-sm text-gray-900 bg-gray-50 rounded-e-lg border-s-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-s-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
          placeholder={placeholder || "1234567890"}
          value={localNumber}
          onChange={handleNumberChange}
        />
      </div>
    </div>
  );
}
